<template>
  <v-container fluid>
    <v-row class="flex-column">
      <v-col cols="12" class="px-0">
        <v-row>
          <v-col cols="12" class="mt-6">
            <p class="reinstateTitle font-bold text-center">
              {{ $t('L_Reinstate_with_the_Original_Issue_Date') }}
            </p>
          </v-col>
          <v-col cols="12" class="mb-4">
            <p class="font-size--14 font-regular text-left">
              {{ $t('L_To_reinstate_your_coverage_using_the_Original_Issue_Date') }}
            </p>
          </v-col>
          <v-col cols="12" class="mb-4 font-bold">
            <h4>{{ $t('L_Follow_the_steps_below_to_complete_the_reinstatement_application') }}</h4>
          </v-col>
          <v-col cols="12" class="blueText font-size--14 font-bold text-left mb-4">
            <ul class="pl-4">
              <li>{{ $t('L_Provide_all_required_information') }}</li>
              <li>
                {{ $t('L_Each_adult_insured_will_need_to_answer_the_medical_history_questions') }}
              </li>
              <li>{{ $t('L_Each_adult_insured_should_sign_the_application') }}</li>
              <li>
                {{
                  $t('L_Mail_the_completed_application_to_the_mailing_address_on_the_application')
                }}
              </li>
            </ul>
          </v-col>
          <v-col cols="12" class="font-size--14 text-left mb-4">
            {{ $t('L_The_reinstatement_application_will_be_mailed_to_the_address_below') }}
          </v-col>
          <v-col cols="12" class="font-size--16 font-bold text-left mb-4">
            <h4>{{ $t('L_Your_Reinstatement_application_forms_will_be_mailed_to') }}</h4>
          </v-col>
          <v-col cols="12" class="font-size--14 font-bold text-left mb-2 d-flex align-center">
            <h5>{{ $t('L_Your_Address') }}</h5>
            <span class="blueReinstatementText ml-6" @click="update = true">
              <v-icon color="#0C6DCA" :icon="mdiPencil"></v-icon>
              {{ $t('L_Update') }}
            </span>
          </v-col>
          <v-col cols="12" v-if="update === false" class="font-size--16 font-weight-regular">
            <div>{{ streetaddress1 }}</div>
            <div>{{ streetaddress2 }}</div>
            <div>{{ city }}, {{ stateData }}</div>
            <div>{{ zip }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="5" v-else>
            <v-form
              ref="submit_reinstatement"
              id="submit_reinstatement"
              v-model="validForm"
              autocomplete="off"
            >
              <OnexAddressLine1
                data-comp-id="update_contact_details_address_one"
                v-model="streetaddress1"
                :label="$t('L_Street_Address_-_Line_1')"
                :maxLength="50"
                required
              />
              <OnexAddressLine2
                data-comp-id="update_contact_details_address_two"
                v-model="streetaddress2"
                :label="$t('L_Street_Address_-_Line_2_optional')"
                :maxLength="50"
              />
              <OnexCity
                data-comp-id="update_contact_details_city"
                v-model="city"
                :label="$t('L_City')"
                :maxLength="50"
                required
              />
              <OnexState
                data-comp-id="update_contact_details_state"
                v-model="stateData"
                :country="currentPolicy.isCanadaPolicy ? 'CA' : 'US'"
                :label="currentPolicy.isCanadaPolicy ? $t('L_policy_province') : $t('L_State')"
                required
              />
              <OnexZip
                id-prop="update_contact_details_zip_code"
                v-model="zip"
                :country="currentPolicy.isCanadaPolicy ? 'CA' : 'US'"
                :label="
                  currentPolicy.isCanadaPolicy ? $t('L_policy_postal_code') : $t('L_Zip_Code')
                "
                required
              />
            </v-form>
          </v-col>
        </v-row>

        <!-- Button -->
        <v-row class="d-flex align-center justify-center py-4">
          <v-col cols="12" sm="5" md="6" class="pr-4">
            <OnexButton
              :disabled="disableActions"
              block
              variant="outlined"
              color="primary"
              @click="backAction"
              :class="{ 'text-button-small': $vuetify.display.width <= 440 && locale === 'fr_CA' }"
            >
              {{ $t('L_back') }}
            </OnexButton>
          </v-col>
          <v-col cols="12" sm="7" md="6" class="pr-4">
            <OnexButton
              :disabled="disableActions || !validForm"
              target="_blank"
              block
              color="primary"
              @click="continueAction"
              :class="{ 'text-button-small': $vuetify.display.width <= 440 && locale === 'fr_CA' }"
            >
              {{ $t('L_Request_Application') }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapStores } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useVuelidate } from '@vuelidate/core';
import { mdiPencil } from '@mdi/js';
import {
  OnexState,
  OnexAddressLine1,
  OnexAddressLine2,
  OnexCity,
  OnexZip,
  OnexButton,
} from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'ReinstatementOriginalIssueDate',
  components: {
    OnexZip,
    OnexState,
    OnexAddressLine1,
    OnexAddressLine2,
    OnexCity,
    OnexButton,
  },
  props: {
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    update: false,
    streetaddress1: '',
    streetaddress2: '',
    city: '',
    stateData: '',
    zip: '',
    $v: useVuelidate(),
    mdiPencil,
    validForm: true,
  }),
  computed: {
    ...mapStores(usePolicyStore, useGlobalStore),
    ...mapState(usePolicyStore, ['currentPolicy']),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
  },
  methods: {
    continueAction() {
      const userObj = {
        streetaddress1: this.streetaddress1,
        streetaddress2: this.streetaddress2,
        city: this.city,
        state: this.stateData,
        zip: this.zip,
      };

      // alert(JSON.stringify(userObj, 1, 3));
      this.$emit('continue-to-the-next-reinstatement-step', userObj);
    },
    backAction() {
      this.$emit('backClicked');
    },
  },
  mounted() {
    this.streetaddress1 = this.currentPolicy?.data?.ownerAddress.address1 || '';
    this.streetaddress2 = this.currentPolicy?.data?.ownerAddress.address2 || '';
    this.city = this.currentPolicy?.data?.ownerAddress.city || '';
    this.stateData = this.currentPolicy?.data?.ownerAddress.state || '';
    this.zip = this.currentPolicy?.data?.ownerAddress.zip || '';
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.reinstateTitle {
  font-size: 22px;
}
.blueReinstatementText {
  color: #0c6dca;
  cursor: pointer;
}
.blueText {
  color: #063562;
}
.text-button-small.v-btn--size-default {
  font-size: 0.875rem;
}
</style>
